import { Link } from 'react-router-dom';
import { 
  ArrowRight, Factory, FlaskConical, Cog, Shield, TrendingUp, 
  Users, Grape, Wine, Package, Settings, Leaf, Scale, 
  Building, Wrench, BarChart3, FileCheck, UserCog
} from 'lucide-react';
import { Button } from '../components/ui/button';

export const Services = () => {
  const mainServices = [
    {
      icon: Factory,
      title: 'Manufacturing Excellence',
      description: 'End-to-end advisory for distillation, blending, bottling, and packaging operations. We optimize your entire manufacturing value chain.',
      features: ['Distillery Operations', 'Brewery Operations', 'Bottling Operations', 'Packaging Operations', 'Utilities Operations'],
    },
    {
      icon: FlaskConical,
      title: 'Distillery & Spirit Projects',
      description: 'Expert guidance on spirit production from grain to glass, including maturation systems and process optimization.',
      features: ['Grain Spirit Projects', 'Malt Spirit Projects', 'Grape Spirit Projects', 'Wine Making Projects', 'Maturation Systems'],
    },
    {
      icon: Cog,
      title: 'Operational Transformation',
      description: 'Drive performance improvement through productivity enhancement, cost optimization, and OEE programs.',
      features: ['Productivity Improvement', 'Cost Optimization', 'OEE Enhancement', 'Debottlenecking', 'Asset Optimization'],
    },
    {
      icon: Shield,
      title: 'Compliance & Governance',
      description: 'Navigate complex regulatory requirements with expert advisory on compliance and excise coordination.',
      features: ['Regulatory Compliance', 'Excise Coordination', 'Governance Advisory', 'Quality Systems', 'Safety Compliance'],
    },
  ];

  const additionalServices = [
    {
      icon: Building,
      title: 'Greenfield Projects',
      description: 'Complete setup support for new manufacturing facilities.',
    },
    {
      icon: Wrench,
      title: 'Brownfield Projects',
      description: 'Upgrade and optimize existing manufacturing operations.',
    },
    {
      icon: BarChart3,
      title: 'Supply Chain Diagnostics',
      description: 'Comprehensive audits and transformation roadmaps.',
    },
    {
      icon: TrendingUp,
      title: 'Performance Programs',
      description: 'SQDCMS-focused operational excellence initiatives.',
    },
    {
      icon: UserCog,
      title: 'Leadership Coaching',
      description: 'Build manufacturing leadership capability.',
    },
    {
      icon: Leaf,
      title: 'Sustainability',
      description: 'Energy efficiency and sustainable practices integration.',
    },
    {
      icon: Package,
      title: 'Import & Export',
      description: 'Complete import and export solutions for alcoholic beverages.',
    },
    {
      icon: Wine,
      title: 'Indian-Flavoured Blends',
      description: 'Creating unique new Indian-flavoured spirit blends.',
    },
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Diagnostic Assessment',
      description: 'Comprehensive evaluation of current operations, identifying gaps and opportunities.',
    },
    {
      number: '02',
      title: 'Strategy Development',
      description: 'Customized transformation roadmap aligned with your business objectives.',
    },
    {
      number: '03',
      title: 'Implementation Support',
      description: 'Hands-on execution guidance with best practices and proven methodologies.',
    },
    {
      number: '04',
      title: 'Capability Building',
      description: 'Training and development to ensure sustainable improvements.',
    },
  ];

  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section data-testid="services-hero" className="relative py-20 lg:py-32 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold mb-4">Our Services</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6">
              Comprehensive<br />
              <span className="text-blue-400">AlcoBev Solutions</span>
            </h1>
            <p className="text-lg lg:text-xl text-slate-300 leading-relaxed">
              From strategy to execution, we provide end-to-end advisory and support 
              for all aspects of manufacturing and supply chain operations in the 
              Alcoholic Beverages industry.
            </p>
          </div>
        </div>
      </section>

      {/* Main Services - Bento Grid */}
      <section data-testid="main-services" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">Core Offerings</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 tracking-tight">
              Our Expertise Areas
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {mainServices.map((service, index) => (
              <div 
                key={index}
                className="bento-item service-card bg-slate-50 p-8 lg:p-10 border border-slate-100 hover:border-blue-100 group"
              >
                <div className="w-16 h-16 bg-blue-600 flex items-center justify-center mb-6 group-hover:bg-slate-900 transition-colors duration-300">
                  <service.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-serif text-2xl lg:text-3xl font-semibold text-slate-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-slate-600 leading-relaxed mb-6">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-slate-700">
                      <span className="w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section data-testid="additional-services" className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">More Solutions</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 tracking-tight">
              Specialized Services
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {additionalServices.map((service, index) => (
              <div 
                key={index}
                className="bento-item bg-white p-8 border border-slate-100 hover:border-blue-100 hover:shadow-lg transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-blue-600/10 flex items-center justify-center mb-5 group-hover:bg-blue-600 transition-colors duration-300">
                  <service.icon className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-slate-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section data-testid="process" className="py-20 lg:py-32 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold mb-4">How We Work</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight">
              Our Consulting Process
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-7xl font-serif font-bold text-blue-600/20 mb-4">
                  {step.number}
                </div>
                <h3 className="font-serif text-xl font-semibold text-white mb-3">
                  {step.title}
                </h3>
                <p className="text-slate-400 leading-relaxed">
                  {step.description}
                </p>
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 right-0 translate-x-1/2">
                    <ArrowRight className="w-6 h-6 text-blue-600/40" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Focus */}
      <section data-testid="industry-focus" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">Industry Focus</p>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-6">
                Specialized in AlcoBev & FMCG Manufacturing
              </h2>
              <p className="text-slate-600 leading-relaxed text-lg mb-8">
                Our expertise spans the entire alcoholic beverages value chain, 
                with deep knowledge of Indian market regulations and global best practices.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Wine, label: 'Spirits' },
                  { icon: Grape, label: 'Wine' },
                  { icon: FlaskConical, label: 'Distilleries' },
                  { icon: Package, label: 'Packaging' },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-100">
                    <item.icon className="w-6 h-6 text-blue-600" />
                    <span className="font-medium text-slate-900">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-full h-full bg-blue-600/10"></div>
              <img 
                src="https://images.pexels.com/photos/34635802/pexels-photo-34635802.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Copper Stills in Distillery"
                className="relative w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="services-cta" className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 tracking-tight mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-slate-600 mb-10 max-w-2xl mx-auto">
            Let's discuss your specific requirements and how we can help transform 
            your manufacturing operations.
          </p>
          <Link to="/contact">
            <Button 
              data-testid="services-cta-contact"
              className="bg-blue-600 text-white hover:bg-blue-700 rounded-none px-10 py-5 text-sm font-bold uppercase tracking-wider btn-shadow"
            >
              Contact Us Today
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
